import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
	PlusIcon,
	PencilIcon,
	TrashIcon,
	ArrowUpIcon,
	ArrowDownIcon,
	EyeIcon,
	EyeSlashIcon
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

const PartnerManagement = () => {
	const [partners, setPartners] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [showModal, setShowModal] = useState(false);
	const [editingPartner, setEditingPartner] = useState(null);
	const [formData, setFormData] = useState({
		name: '',
		logo: null,
		website: '',
		active: true,
		order: 0
	});
	const [formErrors, setFormErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [logoPreview, setLogoPreview] = useState(null);

	useEffect(() => {
		loadPartners();
	}, []);

	const loadPartners = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/api/content/partners/`);
			setPartners(Array.isArray(response.data) ? response.data : (response.data.results || []));
			setError('');
		} catch (err) {
			setError('Error al cargar partners');
			console.error('Error cargando partners:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingPartner(null);
		setFormData({
			name: '',
			logo: null,
			website: '',
			active: true,
			order: partners.length
		});
		setLogoPreview(null);
		setFormErrors({});
		setShowModal(true);
	};

	const handleEdit = (partner) => {
		setEditingPartner(partner);
		setFormData({
			name: partner.name || '',
			logo: null,
			website: partner.website || '',
			active: partner.active !== undefined ? partner.active : true,
			order: partner.order || 0
		});
		setLogoPreview(partner.logo_url || null);
		setFormErrors({});
		setShowModal(true);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setFormErrors({});
		setSaving(true);

		try {
			const formDataToSend = new FormData();
			formDataToSend.append('name', formData.name);
			// Solo agregar logo si hay un archivo nuevo seleccionado
			// Si no hay archivo nuevo y estamos editando, no enviar el campo
			// para que Django mantenga el logo existente
			if (formData.logo) {
				formDataToSend.append('logo', formData.logo);
			}
			if (formData.website !== null && formData.website !== undefined) {
				formDataToSend.append('website', formData.website);
			}
			formDataToSend.append('active', formData.active);
			formDataToSend.append('order', formData.order);

			if (editingPartner) {
				await axios.put(`${API_URL}/api/content/partners/${editingPartner.id}/`, formDataToSend, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			} else {
				await axios.post(`${API_URL}/api/content/partners/`, formDataToSend, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			}

			await loadPartners();
			setShowModal(false);
		} catch (err) {
			if (err.response?.data) {
				setFormErrors(err.response.data);
			} else {
				setError('Error al guardar partner');
			}
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Estás seguro de eliminar este partner?')) return;

		try {
			await axios.delete(`${API_URL}/api/content/partners/${id}/`);
			await loadPartners();
		} catch (err) {
			setError('Error al eliminar partner');
		}
	};

	const handleToggleActive = async (partner) => {
		try {
			await axios.patch(`${API_URL}/api/content/partners/${partner.id}/`, {
				active: !partner.active
			});
			await loadPartners();
		} catch (err) {
			setError('Error al cambiar estado del partner');
		}
	};

	const handleOrderChange = async (partner, direction) => {
		const newOrder = direction === 'up' ? partner.order - 1 : partner.order + 1;
		try {
			await axios.patch(`${API_URL}/api/content/partners/${partner.id}/`, {
				order: newOrder
			});
			await loadPartners();
		} catch (err) {
			setError('Error al cambiar orden');
		}
	};

	const handleLogoChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setFormData({ ...formData, logo: file });
			setLogoPreview(URL.createObjectURL(file));
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900">Gestión de Partners</h1>
						<p className="mt-2 text-sm text-gray-600">
							Administra los partners y logos de la empresa
						</p>
					</div>
					<button
						onClick={handleCreate}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-opacity"
						style={{ backgroundColor: '#12284C' }}
					>
						<PlusIcon className="h-5 w-5" />
						Crear Partner
					</button>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			)}

			{/* Lista de Partners */}
			{loading ? (
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#12284C' }}></div>
					<p className="mt-4 text-gray-600">Cargando partners...</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Orden
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Logo
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Nombre
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Website
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Estado
									</th>
									<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
										Acciones
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{partners.length === 0 ? (
									<tr>
										<td colSpan="6" className="px-6 py-8 text-center text-gray-500">
											No hay partners creados
										</td>
									</tr>
								) : (
									partners.map((partner) => (
										<tr key={partner.id} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="flex items-center gap-2">
													<button
														onClick={() => handleOrderChange(partner, 'up')}
														className="p-1 hover:bg-gray-200 rounded"
														disabled={partner.order === 0}
													>
														<ArrowUpIcon className="h-4 w-4" />
													</button>
													<span className="text-sm font-medium text-gray-900">{partner.order}</span>
													<button
														onClick={() => handleOrderChange(partner, 'down')}
														className="p-1 hover:bg-gray-200 rounded"
													>
														<ArrowDownIcon className="h-4 w-4" />
													</button>
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{partner.logo_url ? (
													<img src={partner.logo_url} alt={partner.name} className="h-12 w-auto object-contain" />
												) : (
													<span className="text-sm text-gray-400">Sin logo</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="text-sm font-medium text-gray-900">{partner.name}</div>
											</td>
											<td className="px-6 py-4">
												<div className="text-sm text-gray-900">
													{partner.website ? (
														<a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
															{partner.website}
														</a>
													) : (
														'-'
													)}
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												{partner.active ? (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
														Activo
													</span>
												) : (
													<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
														Inactivo
													</span>
												)}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
												<div className="flex items-center justify-end gap-2">
													<button
														onClick={() => handleToggleActive(partner)}
														className="p-2 hover:bg-gray-100 rounded"
														title={partner.active ? 'Desactivar' : 'Activar'}
													>
														{partner.active ? (
															<EyeSlashIcon className="h-5 w-5 text-gray-600" />
														) : (
															<EyeIcon className="h-5 w-5 text-gray-600" />
														)}
													</button>
													<button
														onClick={() => handleEdit(partner)}
														className="p-2 hover:bg-gray-100 rounded"
														title="Editar"
													>
														<PencilIcon className="h-5 w-5" style={{ color: '#12284C' }} />
													</button>
													<button
														onClick={() => handleDelete(partner.id)}
														className="p-2 hover:bg-red-50 rounded"
														title="Eliminar"
													>
														<TrashIcon className="h-5 w-5 text-red-600" />
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Modal Crear/Editar */}
			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-2xl font-bold text-gray-900">
									{editingPartner ? 'Editar Partner' : 'Crear Partner'}
								</h2>
								<button
									onClick={() => setShowModal(false)}
									className="text-gray-400 hover:text-gray-600"
								>
									<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSave} className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Nombre *
									</label>
									<input
										type="text"
										required
										value={formData.name}
										onChange={(e) => setFormData({ ...formData, name: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Logo {!editingPartner ? '*' : '(dejar vacío para mantener el actual)'}
									</label>
									<input
										type="file"
										accept="image/*"
										onChange={handleLogoChange}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg"
										required={!editingPartner}
									/>
									{logoPreview && (
										<div className="mt-2">
											<img src={logoPreview} alt="Preview" className="max-w-xs h-auto rounded-lg" />
											{editingPartner && (
												<p className="text-xs text-gray-500 mt-1">
													{formData.logo ? 'Nuevo logo seleccionado' : 'Logo actual'}
												</p>
											)}
										</div>
									)}
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Website
									</label>
									<input
										type="url"
										value={formData.website}
										onChange={(e) => setFormData({ ...formData, website: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
										placeholder="https://..."
									/>
								</div>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Orden
										</label>
										<input
											type="number"
											value={formData.order}
											onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
										/>
									</div>
									<div>
										<label className="flex items-center space-x-2">
											<input
												type="checkbox"
												checked={formData.active}
												onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
												className="rounded border-gray-300"
												style={{ accentColor: '#12284C' }}
											/>
											<span className="text-sm font-medium text-gray-700">Activo</span>
										</label>
									</div>
								</div>

								{Object.keys(formErrors).length > 0 && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										{Object.entries(formErrors).map(([key, value]) => (
											<p key={key} className="text-sm text-red-800">
												{key}: {Array.isArray(value) ? value.join(', ') : value}
											</p>
										))}
									</div>
								)}

								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setShowModal(false)}
										className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
									>
										Cancelar
									</button>
									<button
										type="submit"
										disabled={saving}
										className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
										style={{ backgroundColor: '#12284C' }}
									>
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PartnerManagement;

